import * as THREE from 'three';
import type { ModuleSpec } from './shipSpec';
import type { ModuleDamageState } from './custos';

// Per-module damage on an AUTHORED hull. Its modules draw nothing of their own
// (ModuleSpec.geometry === 'hull'): the whole ship is one GLB, so there is no
// per-module mesh to char. Instead the hull's own materials char the band of
// the spine a wounded module occupies - its z and length, in the frame the
// module groups sit in - with soot that spreads as the module degrades, ember
// cracks while it is red, and its glow put out once it is destroyed.
//
// Nothing here allocates GPU resources: the patch adds uniforms and shader
// text to the ship's own materials, which the ship's dispose() frees.

/** Damaged modules at once; the worst ones keep a slot past this. */
const MAX_BANDS = 16;
/** Soft band edge, engine units either side of a module's end. */
const EDGE = 0.3;

/** 0..1 severity, matching custos's CHAR_TINT ladder (1 - tint). */
const SEVERITY: Record<ModuleDamageState, number> = { green: 0, yellow: 0.3, red: 0.58, destroyed: 0.84 };

export interface HullDamage {
  /** Chain the band onto a material's existing onBeforeCompile (triplanar or none). */
  patch(mat: THREE.Material): void;
  setState(id: string, st: ModuleDamageState): void;
  update(t: number): void;
  dispose(): void;
}

/**
 * @param modules the ship's authored modules (geometry 'hull')
 * @param frame   the group module z is measured in - custos's `visual`
 */
export function createHullDamage(modules: readonly ModuleSpec[], frame: THREE.Object3D): HullDamage {
  const spans = new Map(modules.map((m) => {
    const half = (m.length ?? 0) / 2;
    return [m.id, { z0: m.z - half, z1: m.z + half }];
  }));
  const states = new Map<string, ModuleDamageState>();
  const uniforms = {
    uDmgBand: { value: Array.from({ length: MAX_BANDS }, () => new THREE.Vector4()) },
    uDmgCount: { value: 0 },
    uDmgTime: { value: 0 },
    uDmgShipInv: { value: new THREE.Matrix4() },
  };
  const patched: THREE.Material[] = [];

  function pack(): void {
    const hurt = [...states.entries()]
      .filter(([, st]) => st !== 'green')
      .sort((a, b) => SEVERITY[b[1]] - SEVERITY[a[1]])
      .slice(0, MAX_BANDS);
    hurt.forEach(([id, st], i) => {
      const s = spans.get(id)!;
      uniforms.uDmgBand.value[i]!.set(s.z0, s.z1, SEVERITY[st], st === 'red' ? 1 : 0);
    });
    uniforms.uDmgCount.value = hurt.length;
  }

  function patch(mat: THREE.Material): void {
    const prev = mat.onBeforeCompile.bind(mat);
    const prevKey = mat.customProgramCacheKey();
    mat.onBeforeCompile = (shader, renderer) => {
      prev(shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
uniform mat4 uDmgShipInv;
varying vec3 vDmgPos;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
vDmgPos = ( uDmgShipInv * modelMatrix * vec4( transformed, 1.0 ) ).xyz;`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
uniform vec4 uDmgBand[ ${MAX_BANDS} ];
uniform int uDmgCount;
uniform float uDmgTime;
varying vec3 vDmgPos;
float dmgHash( vec3 p ) {
	p = fract( p * 0.3183099 + 0.1 );
	p *= 17.0;
	return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float dmgNoise( vec3 x ) {
	vec3 i = floor( x ), f = fract( x );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix(
		mix( mix( dmgHash( i ), dmgHash( i + vec3( 1, 0, 0 ) ), f.x ),
		     mix( dmgHash( i + vec3( 0, 1, 0 ) ), dmgHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
		mix( mix( dmgHash( i + vec3( 0, 0, 1 ) ), dmgHash( i + vec3( 1, 0, 1 ) ), f.x ),
		     mix( dmgHash( i + vec3( 0, 1, 1 ) ), dmgHash( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}`)
        .replace('#include <lights_physical_fragment>', `{
	float dmgSoot = 0.0, dmgEmber = 0.0, dmgDead = 0.0;
	float dmgN = dmgNoise( vDmgPos * 1.3 ) * 0.65 + dmgNoise( vDmgPos * 3.4 ) * 0.35;
	for ( int i = 0; i < ${MAX_BANDS}; i ++ ) {
		if ( i >= uDmgCount ) break;
		vec4 b = uDmgBand[ i ];
		float inside = smoothstep( b.x - ${EDGE}, b.x + ${EDGE}, vDmgPos.z )
			* ( 1.0 - smoothstep( b.y - ${EDGE}, b.y + ${EDGE}, vDmgPos.z ) );
		// soot spreads from patches to the whole band as severity climbs
		float t = 0.62 - 0.5 * b.z;
		dmgSoot = max( dmgSoot, inside * smoothstep( t - 0.08, t + 0.08, dmgN ) * ( 0.35 + 0.6 * b.z ) );
		// embers glow in thin cracks through the soot, not over clean hull
		float crack = 1.0 - smoothstep( 0.0, 0.02, abs( dmgNoise( vDmgPos * 3.1 + 7.1 ) - 0.5 ) );
		dmgEmber = max( dmgEmber, inside * b.w * crack * step( 0.2, dmgSoot ) );
		dmgDead = max( dmgDead, inside * step( 0.8, b.z ) );
	}
	diffuseColor.rgb *= 1.0 - dmgSoot;
	roughnessFactor = mix( roughnessFactor, 1.0, dmgSoot );
	metalnessFactor *= 1.0 - 0.7 * dmgSoot;
	totalEmissiveRadiance *= 1.0 - dmgDead;
	float dmgFlick = 0.65 + 0.35 * sin( uDmgTime * 9.0 + dmgN * 23.0 );
	totalEmissiveRadiance += vec3( 1.0, 0.33, 0.07 ) * dmgEmber * dmgFlick * 1.6;
}
#include <lights_physical_fragment>`);
    };
    mat.customProgramCacheKey = () => `${prevKey}|hulldmg-v1`;
    // The band is measured in the module frame, which moves with the ship; the
    // renderer has refreshed matrixWorld by the time any draw call runs.
    mat.onBeforeRender = () => { uniforms.uDmgShipInv.value.copy(frame.matrixWorld).invert(); };
    mat.needsUpdate = true;
    patched.push(mat);
  }

  return {
    patch,
    setState(id, st) {
      if (!spans.has(id)) return;
      if (st === 'green') states.delete(id); else states.set(id, st);
      pack();
    },
    update(t) { uniforms.uDmgTime.value = t; },
    dispose() {
      for (const m of patched) m.onBeforeRender = () => {};
      patched.length = 0;
      states.clear();
      uniforms.uDmgCount.value = 0;
    },
  };
}
